import React, { useState, useEffect } from 'react';
import api from '../api/axios';
import Loading from '../components/Loading';
import CreateClientModal from '../components/CreateClientModal';
import { Enrollment, Transaction, Client, TransactionStatus } from '../types';
import './Pages.css';

interface ClientDetailsResponse {
  client: Client;
  enrollment: Enrollment;
  transactions: Transaction[];
}

interface ClientWithGamification {
  clientId: string;
  name: string;
  email: string;
  phone?: string;
  totalPoints: number;
  gamificationLevel: number;
  gamificationLevelName: string;
  enrollments: Array<{
    enrollmentId: string;
    planId: string;
    planName: string;
    points: number;
    isActive: boolean;
    enrolledAt: string;
  }>;
}

const levelEmojis: Record<string, string> = {
  'Starter': '🌱',
  'Bronze': '🥉',
  'Silver': '🥈',
  'Gold': '🥇',
  'Platinum': '💎',
  'Diamond': '💠',
  'Black': '⬛',
  'Infinity': '♾️',
  'Legend': '👑',
  'Founders': '🏆'
};

const Clients: React.FC = () => {
  const [clients, setClients] = useState<ClientWithGamification[]>([]);
  const [filteredClients, setFilteredClients] = useState<ClientWithGamification[]>([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [clientDetails, setClientDetails] = useState<ClientDetailsResponse | null>(null);
  const [loadingDetails, setLoadingDetails] = useState(false);
  const [searchName, setSearchName] = useState('');
  const [filterLevel, setFilterLevel] = useState<number | null>(null);

  useEffect(() => {
    loadClients();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    applyFilters();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clients, searchName, filterLevel]);

  const loadClients = async () => {
    try {
      const response = await api.get<ClientWithGamification[]>('/admin/clients');
      setClients(response.data);
    } catch (error) {
      console.error('Error loading clients:', error);
    } finally {
      setLoading(false);
    }
  };

  const applyFilters = () => {
    let filtered = [...clients];

    if (searchName) {
      const searchLower = searchName.toLowerCase();
      filtered = filtered.filter(c =>
        c.name.toLowerCase().includes(searchLower) ||
        c.email.toLowerCase().includes(searchLower)
      );
    }

    if (filterLevel !== null) {
      filtered = filtered.filter(c => c.gamificationLevel === filterLevel);
    }

    setFilteredClients(filtered);
  };

  const loadClientDetails = async (clientId: string) => {
    setLoadingDetails(true);
    try {
      const response = await api.get<ClientDetailsResponse>(`/admin/clients/${clientId}/details`);
      setClientDetails(response.data);
      setShowModal(true);
    } catch (error) {
      console.error('Error loading client details:', error);
      alert('Erro ao carregar detalhes do cliente');
    } finally {
      setLoadingDetails(false);
    }
  };

  const handleClientClick = (clientId: string) => {
    loadClientDetails(clientId);
  };

  const closeModal = () => {
    setShowModal(false);
    setClientDetails(null);
  };

  if (loading) return <Loading message="Carregando clientes..." />;

  return (
    <div className="page">
      <div className="page-header">
        <div>
          <h1>Clientes</h1>
          <p>Visualize todos os clientes cadastrados no seu programa</p>
        </div>
        <button 
          className="btn-primary"
          onClick={() => setShowCreateModal(true)}
        >
          ➕ Cadastrar Novo Cliente
        </button>
      </div>

      <div className="filters-section" style={{ marginBottom: '24px', display: 'flex', gap: '16px', flexWrap: 'wrap' }}>
        <div style={{ flex: '1', minWidth: '250px' }}>
          <input
            type="text"
            placeholder="🔍 Buscar por nome..."
            value={searchName}
            onChange={(e) => setSearchName(e.target.value)}
            style={{
              width: '100%',
              padding: '12px 16px',
              border: '2px solid var(--border)',
              borderRadius: '12px',
              fontSize: '15px',
              transition: 'all 0.3s'
            }}
          />
        </div>
        <div>
          <select
            value={filterLevel ?? ''}
            onChange={(e) => setFilterLevel(e.target.value ? parseInt(e.target.value) : null)}
            style={{
              padding: '12px 16px',
              border: '2px solid var(--border)',
              borderRadius: '12px',
              fontSize: '15px',
              backgroundColor: 'white',
              cursor: 'pointer'
            }}
          >
            <option value="">Todos os níveis</option>
            {Object.entries(levelEmojis).map(([name, emoji], index) => (
              <option key={index + 1} value={index + 1}>
                {emoji} {name}
              </option>
            ))}
          </select>
        </div>
        {(searchName || filterLevel !== null) && (
          <button
            onClick={() => {
              setSearchName('');
              setFilterLevel(null);
            }}
            style={{
              padding: '12px 20px',
              border: 'none',
              borderRadius: '12px',
              backgroundColor: '#F3F4F6',
              color: '#6B7280',
              fontWeight: '600',
              cursor: 'pointer'
            }}
          >
            Limpar filtros
          </button>
        )}
      </div>

      <div className="clients-table">
        <div className="table-header" style={{ gridTemplateColumns: '2fr 1fr 1fr 1fr' }}>
          <div>Cliente</div>
          <div>Pontos Totais</div>
          <div>Nível</div>
          <div>Planos Ativos</div>
        </div>
        {filteredClients.map((client) => (
          <div 
            key={client.clientId} 
            className="table-row"
            style={{ cursor: 'pointer', gridTemplateColumns: '2fr 1fr 1fr 1fr' }}
            onClick={() => handleClientClick(client.clientId)}
          >
            <div className="client-info">
              <div className="client-avatar">
                {client.name.substring(0, 2).toUpperCase()}
              </div>
              <div>
                <div className="client-name">{client.name}</div>
                <div className="client-since">
                  {client.email}
                </div>
              </div>
            </div>
            <div className="current-points">{client.totalPoints} pts</div>
            <div>
              <span className="level-badge">
                {levelEmojis[client.gamificationLevelName]} {client.gamificationLevelName}
              </span>
            </div>
            <div>{client.enrollments.filter(e => e.isActive).length}</div>
          </div>
        ))}
      </div>

      {filteredClients.length === 0 && !loading && (
        <div className="empty-state">
          <div className="empty-icon">👥</div>
          <h3>{clients.length === 0 ? 'Nenhum cliente cadastrado' : 'Nenhum cliente encontrado'}</h3>
          <p>{clients.length === 0 ? 'Quando clientes se cadastrarem, eles aparecerão aqui' : 'Tente ajustar os filtros de busca'}</p>
        </div>
      )}

      {showModal && (
        <div className="modal-overlay" onClick={closeModal}>
          <div className="modal client-details-modal" onClick={(e) => e.stopPropagation()}>
            {loadingDetails ? (
              <Loading message="Carregando detalhes..." />
            ) : clientDetails ? (
              <>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '28px' }}>
                  <h2>Detalhes do Cliente</h2>
                  <button
                    onClick={closeModal}
                    style={{
                      background: 'none',
                      border: 'none',
                      fontSize: '24px',
                      cursor: 'pointer',
                      color: '#666',
                      padding: '0',
                      width: '32px',
                      height: '32px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}
                  >
                    ×
                  </button>
                </div>

                <div className="client-details-section">
                  <h3 style={{ marginBottom: '20px', fontSize: '20px', fontWeight: 700, color: 'var(--text-dark)' }}>
                    Informações do Cliente
                  </h3>
                  <div className="detail-row">
                    <span className="label">Nome:</span>
                    <span>{clientDetails.client.name || 'N/A'}</span>
                  </div>
                  <div className="detail-row">
                    <span className="label">Telefone:</span>
                    <span>{clientDetails.client.phone}</span>
                  </div>
                  {clientDetails.client.email && (
                    <div className="detail-row">
                      <span className="label">Email:</span>
                      <span>{clientDetails.client.email}</span>
                    </div>
                  )}
                  {clientDetails.client.cpf && (
                    <div className="detail-row">
                      <span className="label">CPF:</span>
                      <span>{clientDetails.client.cpf}</span>
                    </div>
                  )}
                  <div className="detail-row">
                    <span className="label">Cadastrado em:</span>
                    <span>{new Date(clientDetails.client.createdAt).toLocaleDateString('pt-BR')}</span>
                  </div>
                </div>

                <div className="client-details-section" style={{ marginTop: '32px' }}>
                  <h3 style={{ marginBottom: '20px', fontSize: '20px', fontWeight: 700, color: 'var(--text-dark)' }}>
                    Informações de Pontuação
                  </h3>
                  <div className="detail-row">
                    <span className="label">Pontos Atuais:</span>
                    <span className="points">{clientDetails.enrollment.currentPoints} pts</span>
                  </div>
                  <div className="detail-row">
                    <span className="label">Total Acumulado:</span>
                    <span className="points">{clientDetails.enrollment.totalPointsEarned} pts</span>
                  </div>
                  <div className="detail-row">
                    <span className="label">Recompensas Resgatadas:</span>
                    <span>{clientDetails.enrollment.rewardsRedeemed}</span>
                  </div>
                  <div className="detail-row">
                    <span className="label">Status:</span>
                    <span>
                      <span className={`status-badge ${clientDetails.enrollment.isActive ? 'active' : 'inactive'}`}>
                        {clientDetails.enrollment.isActive ? 'Ativo' : 'Inativo'}
                      </span>
                    </span>
                  </div>
                  <div className="detail-row">
                    <span className="label">Inscrito desde:</span>
                    <span>{new Date(clientDetails.enrollment.enrolledAt).toLocaleDateString('pt-BR')}</span>
                  </div>
                </div>

                <div className="client-details-section" style={{ marginTop: '32px' }}>
                  <h3 style={{ marginBottom: '20px', fontSize: '20px', fontWeight: 700, color: 'var(--text-dark)' }}>
                    Transações ({clientDetails.transactions.length})
                  </h3>
                  {clientDetails.transactions.length === 0 ? (
                    <div style={{ padding: '20px', textAlign: 'center', color: 'var(--text-gray)' }}>
                      Nenhuma transação encontrada
                    </div>
                  ) : (
                    <div className="transactions-table" style={{ maxHeight: '400px', overflowY: 'auto' }}>
                      {clientDetails.transactions.map((transaction) => {
                        const isRedemption = transaction.source === 'Resgate';
                        return (
                          <div 
                            key={transaction.transactionId} 
                            className="transaction-card" 
                            style={{ 
                              padding: '20px',
                              border: isRedemption ? '3px solid #F59E0B' : undefined,
                              backgroundColor: isRedemption ? '#FFFBEB' : undefined
                            }}
                          >
                            {isRedemption && (
                              <div style={{ 
                                marginBottom: '12px', 
                                padding: '8px 12px', 
                                backgroundColor: '#F59E0B', 
                                color: 'white', 
                                borderRadius: '6px',
                                fontWeight: 600,
                                fontSize: '14px',
                                display: 'inline-block'
                              }}>
                                🎁 Resgate
                              </div>
                            )}
                            <div className="transaction-details">
                              <div className="detail-row">
                                <span className="label">Data:</span>
                                <span>{new Date(transaction.transactionDate).toLocaleDateString('pt-BR')}</span>
                              </div>
                              <div className="detail-row">
                                <span className="label">Valor:</span>
                                <span className="amount">R$ {transaction.amount.toFixed(2)}</span>
                              </div>
                              <div className="detail-row">
                                <span className="label">Pontos:</span>
                                <span className="points" style={{ color: isRedemption ? '#DC2626' : undefined, fontWeight: isRedemption ? 700 : undefined }}>
                                  {transaction.pointsEarned} pts
                                </span>
                              </div>
                              <div className="detail-row">
                                <span className="label">Status:</span>
                                <span>
                                  <span className={`status-badge ${
                                    transaction.status === TransactionStatus.Approved ? 'active' : 
                                    transaction.status === TransactionStatus.Rejected ? 'inactive' : 
                                    'pending'
                                  }`}>
                                    {transaction.status === TransactionStatus.Approved ? 'Aprovada' : 
                                     transaction.status === TransactionStatus.Rejected ? 'Rejeitada' : 
                                     'Pendente'}
                                  </span>
                                </span>
                              </div>
                              {transaction.rejectionReason && (
                                <div className="detail-row">
                                  <span className="label">Motivo da Rejeição:</span>
                                  <span style={{ color: '#EF4444' }}>{transaction.rejectionReason}</span>
                                </div>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>

                <div className="modal-actions">
                  <button className="btn-secondary" onClick={closeModal}>
                    Fechar
                  </button>
                </div>
              </>
            ) : null}
          </div>
        </div>
      )}

      {showCreateModal && (
        <CreateClientModal
          onClose={() => setShowCreateModal(false)}
          onSuccess={() => {
            setShowCreateModal(false);
            loadClients();
          }}
        />
      )}
    </div>
  );
};

export default Clients;


